import * as CL from '@design-system/component-library';
import { RelatedPartyRoles } from '../../common/enums.js';
import {
  approverMsg,
  customerMsg,
  deliveryFeeMsg,
  monthlyChargesMsg,
  oneTimePaymentMsg,
  orderedProductsMsg,
  productsMsg,
  statusMsg,
  t,
  totalMonthlyChargesVatZeroMsg,
  totalOneTimePaymentsMsg,
  typeMsg,
} from '../../common/i18n/index.js';
import { getOrderStateInfo, getPartyName } from '../CustomerOrderList/cocoOrderUtils.js';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import { Status } from '../Status/Status.js';
import {
  formatMonthlyPriceAmount,
  formatPriceAmount,
  getOrderItemPriceTotal,
  getPriceTotal,
} from './OrderContentCoCoUtils.js';
import type { OrderContentCoCoProps } from './OrderContentCoCoUtils.js';
import type { CLDescriptionItem, CLTableColumn } from '../../common/types/componentLibrary';
import type { CocoProductOrderLineItem } from '../../generated/api/cocoProductOrderLineItem.js';

const OrderedProductsTable = ({ items }: { items: CocoProductOrderLineItem[] }) => {
  const columns: CLTableColumn[] = [
    { key: 'product', label: t.IY4Z(productsMsg) },
    { key: 'monthlyCharges', label: t.P6BC(monthlyChargesMsg) },
    { key: 'oneTimePayment', label: t.GOBY(oneTimePaymentMsg) },
    { key: 'type', label: t.F9G1(typeMsg) },
    { key: 'status', label: t.ASQT(statusMsg) },
  ];
  const rows = items.map(item => {
    const itemState = getOrderStateInfo(item.state);
    return {
      product: item.productOffering.name,
      monthlyCharges: formatMonthlyPriceAmount(getOrderItemPriceTotal(item, PriceType.RECURRING_CHARGE)),
      oneTimePayment: formatPriceAmount(getOrderItemPriceTotal(item, PriceType.ONE_TIME_CHARGE)),
      type: item.productOffering.productOfferingType.category,
      status: <Status color={itemState.color} textAfterBadge={itemState.text} />,
    };
  });

  return (
    <>
      <h3>{t.TBES(orderedProductsMsg)}</h3>
      <div className="of-table-wrapper">
        <CL.Table columns={columns} hideHeadersOnMobile={false} rows={rows} tableType="bordered" hover={true} />
      </div>
    </>
  );
};

export const NewOrderContentCoCo = ({ order }: OrderContentCoCoProps) => {
  const orderState = getOrderStateInfo(order.orderState);
  const deliveryPrices =
    order.deliveryOrderItem?.flatMap(item =>
      item.price.filter(price => price.priceType === PriceType.ONE_TIME_CHARGE).map(price => price.priceWithoutTax)
    ) ?? [];
  const deliveryFeeTotal =
    deliveryPrices.length > 0 ? deliveryPrices.reduce((total, price) => total + price, 0) : undefined;
  const statusItem: CLDescriptionItem = {
    title: t.ASQT(statusMsg),
    description: <Status color={orderState.color} textAfterBadge={orderState.text} />,
  };
  const descriptionItems: CLDescriptionItem[] = [
    statusItem,
    {
      title: t.IY5V(customerMsg),
      description: getPartyName(order, RelatedPartyRoles.CUSTOMER),
    },
    {
      title: t.E0K4(totalMonthlyChargesVatZeroMsg),
      description: formatMonthlyPriceAmount(getPriceTotal(order.price, PriceType.RECURRING_CHARGE)),
    },
    {
      title: t.TE4L(totalOneTimePaymentsMsg),
      description: formatPriceAmount(getPriceTotal(order.price, PriceType.ONE_TIME_CHARGE)),
    },
    {
      title: t.PFDS(deliveryFeeMsg),
      description: formatPriceAmount(deliveryFeeTotal),
    },
    {
      title: t.ARD5(approverMsg),
      description: getPartyName(order, RelatedPartyRoles.APPROVER),
    },
  ];

  return (
    <div className="of-customer-order-details__content">
      <CL.Description items={descriptionItems} />
      <OrderedProductsTable items={order.productOrderItem} />
    </div>
  );
};
